import { useState, useEffect } from 'react';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

/* ─── Dark mode (same pattern as Landing) ─── */
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
};

/* ─── Topographic contour background — the atlas signature ─── */
/* ─── Data ─── */
const PROVIDERS = [
  { name: 'Supabase', role: 'Database and authentication',
    detail: 'Stores your account information, learning progress, and scores. Data is encrypted at rest and in transit. Supabase operates on AWS infrastructure.' },
  { name: 'DeepSeek', role: 'AI scoring and response generation',
    detail: "Your prompt submissions are sent to DeepSeek's API to generate educational responses and quality scores. DeepSeek processes this data under their own privacy policy. We do not send identifying information (name, email) alongside your prompts — only the prompt text and exercise context." },
  { name: 'PostHog', role: 'Analytics',
    detail: 'Collects anonymized usage data to help us understand how the platform is used. No personally identifiable information is sent to PostHog.' },
  { name: 'Stripe (future)', role: 'Payment processing',
    detail: 'If and when paid tiers are introduced, payments will be handled by Stripe. Castle Atlas will never store your financial account details — these go directly to Stripe.' },
  { name: 'Vercel', role: 'Frontend hosting',
    detail: 'Serves the Castle Atlas web application. May process request metadata (IP address, headers) as part of standard web hosting.' },
  { name: 'Railway', role: 'Backend hosting',
    detail: 'Hosts the Castle Atlas API server. Processes requests between your browser and our database.' },
];

const SECTIONS = [
  'Information we collect', 'How we use your information', 'Legal basis for processing (GDPR)',
  'How we share your information', "Children's privacy", 'Cookies', 'Data retention',
  'Security', 'International data transfers', 'Your rights', 'California residents (CCPA)',
  'Changes to this policy', 'Contact',
];

const Privacy = () => {
  usePageTitle('Privacy Policy — Castle Atlas');
  const isDark = useDarkMode();

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card:   isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head:   isDark ? '#fafaf9' : '#1c1917',
    body:   isDark ? '#a8a29e' : '#57534e',
    muted:  isDark ? '#78716c' : '#78716c',
    strong: isDark ? '#d6d3d1' : '#44403c',
    dash:   isDark ? '#57534e' : '#d4cdc7',
    inner:  isDark ? '#151211' : '#f5f2ee',
  };

  /* Shared building blocks */
  const H2 = ({ n, children, id }) => (
    <h2 id={id} style={{ display:'flex',alignItems:'baseline',gap:12,fontFamily:'var(--font-display)',fontSize:19,fontWeight:800,color:c.head,letterSpacing:'-0.01em',margin:'0 0 14px',scrollMarginTop:90 }}>
      <span style={{ fontFamily:'var(--font-mono)',fontSize:12,fontWeight:600,color:'#ea580c',letterSpacing:'0.1em',flexShrink:0 }}>§ {String(n).padStart(2,'0')}</span>
      {children}
    </h2>
  );
  const P = ({ children, mb = 12 }) => (
    <p style={{ fontSize:14,color:c.body,lineHeight:1.75,margin:`0 0 ${mb}px` }}>{children}</p>
  );
  const List = ({ items }) => (
    <ul style={{ listStyle:'none',padding:0,margin:'0 0 12px',display:'flex',flexDirection:'column',gap:9 }}>
      {items.map((item,i) => (
        <li key={i} style={{ display:'flex',gap:10,fontSize:14,color:c.body,lineHeight:1.7 }}>
          <span style={{ color:'#ea580c',flexShrink:0,fontFamily:'var(--font-mono)' }}>—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
  const Mail = ({ subject }) => (
    <a href={`mailto:hello@castleatlas.co${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`}
      style={{ color:'#ea580c',fontWeight:600,textDecoration:'none' }}>hello@castleatlas.co</a>
  );
  const Section = ({ children }) => (
    <section style={{ borderTop:`1px solid ${c.border}`,paddingTop:32 }}>{children}</section>
  );

  return (
    <div id="main-content" tabIndex={-1} style={{ minHeight:'100vh',background:c.pageBg,fontFamily:'var(--font-body)' }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        .lg1 { animation: fadeUp 0.4s ease-out both; }
        .lg2 { animation: fadeUp 0.4s ease-out 0.08s both; }
        .lg3 { animation: fadeUp 0.4s ease-out 0.16s both; }
        @media(prefers-reduced-motion:reduce){ .lg1,.lg2,.lg3 { animation:none!important; } }
      `}</style>

      {/* ── Header — cartographic ── */}
      <div style={{ position:'relative',overflow:'hidden',borderBottom:`1px solid ${c.border}` }}>
        <ContourBG isDark={isDark}/>
        <div className="lg1" style={{ maxWidth:760,margin:'0 auto',padding:'72px 24px 48px',position:'relative',zIndex:2 }}>
          <div style={{ display:'inline-flex',alignItems:'center',gap:10,marginBottom:16 }}>
            <span style={{ fontFamily:'var(--font-mono)',fontSize:11,fontWeight:600,color:'#ea580c',letterSpacing:'0.14em' }}>RECORDS</span>
            <span style={{ width:22,height:1,background:c.dash,display:'inline-block' }}/>
            <span style={{ fontFamily:'var(--font-mono)',fontSize:11,fontWeight:600,color:c.muted,letterSpacing:'0.14em',textTransform:'uppercase' }}>Privacy Policy</span>
          </div>
          <h1 style={{ fontFamily:'var(--font-display)',fontSize:'clamp(2rem,5vw,2.8rem)',fontWeight:900,color:c.head,letterSpacing:'-0.03em',lineHeight:1.08,margin:'0 0 14px' }}>
            What we know about you,<br/>and what we do with it.
          </h1>
          <p style={{ fontFamily:'var(--font-mono)',fontSize:11,color:c.muted,letterSpacing:'0.08em',margin:0 }}>
            LAST UPDATED · MAY 9, 2026
          </p>
        </div>
      </div>

      <div style={{ maxWidth:760,margin:'0 auto',padding:'40px 24px 96px' }}>

        {/* ── Index of sections ── */}
        <div className="lg2" style={{ background:c.card,border:`1px solid ${c.border}`,borderRadius:20,padding:'24px 26px',marginBottom:40 }}>
          <div style={{ fontFamily:'var(--font-mono)',fontSize:10,fontWeight:600,color:c.muted,letterSpacing:'0.14em',marginBottom:14 }}>INDEX OF SECTIONS</div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'8px 20px' }}>
            {SECTIONS.map((s,i) => (
              <a key={s} href={`#s${i+1}`} style={{ display:'flex',gap:9,fontSize:13,color:c.body,textDecoration:'none',lineHeight:1.5 }}
                onMouseEnter={e=>e.currentTarget.style.color='#ea580c'} onMouseLeave={e=>e.currentTarget.style.color=c.body}>
                <span style={{ fontFamily:'var(--font-mono)',fontSize:11,color:'#ea580c',flexShrink:0 }}>{String(i+1).padStart(2,'0')}</span>
                {s}
              </a>
            ))}
          </div>
        </div>

        <div className="lg3" style={{ display:'flex',flexDirection:'column',gap:32 }}>

          <p style={{ fontSize:15,color:c.strong,lineHeight:1.8,margin:0 }}>
            Castle Atlas ("we", "us", or "our") operates the learning platform at castleatlas.co.
            This Privacy Policy explains what personal information we collect, how we use it, who
            we share it with, and what rights you have over it. We've written this to be readable,
            not just legally defensible.
          </p>

          <Section>
            <H2 n={1} id="s1">Information we collect</H2>
            <P>We collect the following categories of information:</P>
            <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
              {[
                ['Account information','Your name, email address, and password when you create an account. You may optionally add profile details, but these are not required.'],
                ['Learning activity','The exercises you attempt and complete, your scores, prompts you submit for evaluation, and your progress through courses and lessons.'],
                ['Voice Room session data','If you use the Voice Room feature, we process your microphone input in real time to simulate conversation. We do not record or store your voice. Conversation transcripts within a session are discarded when the session ends unless you choose to save them.'],
                ['Usage and analytics data','Pages visited, features used, session duration, and interaction patterns, collected through PostHog analytics. This data is anonymized and used in aggregate. We do not sell it.'],
                ['Technical data','IP address, browser type, device type, and operating system, collected automatically when you access the Service. This is used for security, debugging, and platform compatibility.'],
              ].map(([title,text]) => (
                <div key={title}>
                  <p style={{ fontSize:13.5,fontWeight:700,color:c.strong,margin:'0 0 4px' }}>{title}</p>
                  <p style={{ fontSize:14,color:c.body,lineHeight:1.7,margin:0 }}>{text}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section>
            <H2 n={2} id="s2">How we use your information</H2>
            <P>We use the information we collect to:</P>
            <List items={[
              'Create and maintain your account and provide access to the Service',
              'Track and display your learning progress, scores, and streaks',
              'Send your prompt submissions to our AI scoring system and return feedback to you',
              'Send transactional emails (account confirmation, password reset, important service updates)',
              'Send product update or marketing emails — only with your explicit consent, and you can unsubscribe at any time',
              'Detect and prevent fraud, abuse, and security incidents',
              'Improve our curriculum, scoring algorithms, and platform features using anonymized, aggregated data',
              'Comply with legal obligations',
            ]}/>
            <P mb={0}>
              We will never use your personal information for purposes materially different from those
              listed above without first notifying you and, where required by law, obtaining your consent.
            </P>
          </Section>

          <Section>
            <H2 n={3} id="s3">Legal basis for processing (GDPR)</H2>
            <P>
              If you are in the European Economic Area (EEA), the UK, or another jurisdiction with similar
              requirements, we only process your personal information when we have a valid legal basis:
            </P>
            <List items={[
              'Contractual necessity — we need your information to provide the Service you signed up for',
              'Legitimate interests — to improve our platform, keep it secure, and understand usage patterns, provided your rights do not override these interests',
              'Consent — for marketing emails and non-essential analytics cookies',
              'Legal obligation — when required by tax, accounting, or other applicable laws',
            ]}/>
          </Section>

          <Section>
            <H2 n={4} id="s4">How we share your information</H2>
            <P>
              Castle Atlas is built on several third-party services. We are transparent about who they
              are and what they do with your data:
            </P>
            <div style={{ display:'flex',flexDirection:'column',gap:12,margin:'0 0 16px' }}>
              {PROVIDERS.map(p => (
                <div key={p.name} style={{ background:c.card,border:`1px solid ${c.border}`,borderRadius:16,padding:'16px 18px' }}>
                  <div style={{ display:'flex',alignItems:'baseline',gap:9,marginBottom:5,flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'var(--font-display)',fontSize:14,fontWeight:800,color:c.head }}>{p.name}</span>
                    <span style={{ fontFamily:'var(--font-mono)',fontSize:10,fontWeight:600,color:'#ea580c',letterSpacing:'0.1em',textTransform:'uppercase' }}>{p.role}</span>
                  </div>
                  <p style={{ fontSize:13.5,color:c.body,lineHeight:1.7,margin:0 }}>{p.detail}</p>
                </div>
              ))}
            </div>
            <P>We may also share your information in the following circumstances:</P>
            <List items={[
              'As required by law — to comply with a subpoena, court order, or other legal process, or to protect the rights, property, or safety of Castle Atlas, our users, or the public',
              'Business transfers — if Castle Atlas is acquired by or merged with another company, or if we sell or transfer all or part of our assets, your information may be transferred as part of that transaction',
              'With your consent — when you explicitly ask us to share your information',
            ]}/>
            <P mb={0}>
              We do not sell your personal information to any third party. We do not share your data
              with advertisers.
            </P>
          </Section>

          <Section>
            <H2 n={5} id="s5">Children's privacy</H2>
            <P mb={0}>
              Castle Atlas is not directed at children under the age of 13. We do not knowingly collect
              personal information from children under 13. If you are a parent or guardian and believe
              your child has provided us with personal information, please contact us immediately at{' '}
              <Mail/> and we will delete that information promptly.
            </P>
          </Section>

          <Section>
            <H2 n={6} id="s6">Cookies</H2>
            <P>We use two categories of cookies:</P>
            <List items={[
              <><strong style={{ color:c.strong }}>Essential cookies:</strong> Required for authentication and keeping you logged in. The Service cannot function without these.</>,
              <><strong style={{ color:c.strong }}>Analytics cookies:</strong> Used by PostHog to collect anonymized usage data. You can opt out by enabling "Do Not Track" in your browser settings.</>,
            ]}/>
            <P mb={0}>We do not use advertising cookies or sell cookie data to any third party.</P>
          </Section>

          <Section>
            <H2 n={7} id="s7">Data retention</H2>
            <P>
              We retain your personal information for as long as your account is active, or as needed to
              provide the Service and comply with our legal obligations.
            </P>
            <P mb={0}>
              When you delete your account, we will permanently delete your personal information within
              30 days of your request. Anonymized and aggregated learning data (with no link to your
              identity) may be retained for platform improvement purposes. Some data may be retained
              longer where required by applicable law, such as for financial record-keeping if paid
              subscriptions are involved.
            </P>
          </Section>

          <Section>
            <H2 n={8} id="s8">Security</H2>
            <P mb={0}>
              We use administrative, technical, and physical safeguards designed to protect your personal
              information against unauthorized access, alteration, disclosure, or destruction. These
              include encrypted data storage (Supabase), HTTPS-only transmission, and access controls on
              our backend systems. However, no method of transmission over the internet or electronic
              storage is 100% secure. We cannot guarantee absolute security. If we become aware of a
              security breach that affects your personal information, we will notify you electronically
              within a reasonable time and take immediate steps to contain and investigate the incident.
            </P>
          </Section>

          <Section>
            <H2 n={9} id="s9">International data transfers</H2>
            <P mb={0}>
              Castle Atlas operates globally. Your personal information may be transferred to and
              processed in countries other than your own, including the United States, where our
              infrastructure providers (Supabase, Vercel, Railway) operate their servers. These countries
              may have different data protection laws than your jurisdiction. By using the Service, you
              consent to this transfer. We take steps to ensure your data is handled in accordance with
              this Privacy Policy regardless of where it is processed.
            </P>
          </Section>

          <Section>
            <H2 n={10} id="s10">Your rights</H2>
            <P>
              Depending on where you are located, you may have the following rights regarding your
              personal information:
            </P>
            <List items={[
              'Access — request a copy of the personal information we hold about you',
              'Correction — request that we correct inaccurate or incomplete information',
              'Deletion — request that we delete your personal information (see Section 7)',
              'Portability — request your data in a machine-readable format',
              'Objection — object to certain uses of your data, including marketing',
              'Restriction — request that we limit how we process your data in certain circumstances',
            ]}/>
            <P>
              These rights apply to users in the European Union (GDPR), United Kingdom (UK GDPR),
              California (CCPA), and other jurisdictions with applicable data protection legislation.
              We extend good-faith compliance to all users regardless of location.
            </P>
            <P mb={0}>
              To exercise any of these rights, email us at <Mail/>. We will respond within 30 days.
              You may also delete your account directly from Settings → Account at any time.
            </P>
          </Section>

          <Section>
            <H2 n={11} id="s11">California residents (CCPA)</H2>
            <P>
              If you are a California resident, you have specific rights under the California Consumer
              Privacy Act (CCPA):
            </P>
            <List items={[
              'The right to know what personal information we collect, use, disclose, and sell',
              'The right to delete personal information we have collected from you',
              'The right to opt out of the sale of your personal information — we do not sell your data',
              'The right not to be discriminated against for exercising your CCPA rights',
            ]}/>
            <P mb={0}>
              To submit a CCPA request, email <Mail subject="CCPA Request"/> with "CCPA Request" in the subject line.
            </P>
          </Section>

          <Section>
            <H2 n={12} id="s12">Changes to this policy</H2>
            <P mb={0}>
              We may update this Privacy Policy from time to time. For material changes — particularly
              those that affect how we collect, use, or share your personal information — we will notify
              you by email or via a prominent notice on the platform at least 30 days before the change
              takes effect. The "Last updated" date at the top of this page reflects the most recent
              revision. Continued use of the Service after the effective date constitutes acceptance of
              the updated policy.
            </P>
          </Section>

          <Section>
            <H2 n={13} id="s13">Contact</H2>
            <P mb={0}>
              Questions, requests, or concerns about this Privacy Policy? We're reachable at <Mail/>.
              We take privacy seriously and will respond to every message.
            </P>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Privacy;
